/**
 * mediaPaste — reusable Ctrl/Cmd+V handler for narrative textareas
 * ─────────────────────────────────────────────────────────────────
 *
 * 2026-05-21 — when a consultant pastes an image or video from the
 * clipboard into a narrative textarea (Executive Summary, finding
 * Description / Impact / Remediation, etc.) we want the renderer to
 * embed it inline. The renderer (packages/renderer/src/htmlBuilder.ts
 * inlineMarkdown) already accepts two markdown forms — but ONLY when
 * the src is a data: URI:
 *
 *   ![alt](data:image/png;base64,...)   → centred <img>
 *   [video:data:video/mp4;base64,...]   → centred <video controls>
 *
 * Anything else (filenames, relative paths) falls back to the
 * `[image: foo.png]` / `[video: omitted]` muted placeholder. That's
 * what consultants were seeing — Pre-populate had inserted text with
 * filenames, but the textareas had no way to convert a pasted file
 * into a data URI.
 *
 * This module fills that gap. `handleMediaPaste` is wired to the
 * `onPaste` prop of every narrative textarea. When clipboard data
 * contains an image/video file, it:
 *
 *   1. Reads the blob into an ArrayBuffer (single I/O, gentle on RAM).
 *   2. Encodes it to base64 in 32 KB chunks (avoids the GC pressure
 *      that broke FileReader.readAsDataURL on large videos —
 *      see FindingsScreen.tsx uploadEvidenceBlob).
 *   3. Splices the markdown embed at the current cursor position,
 *      wrapped in blank lines so it sits in its own paragraph.
 *   4. Surfaces a friendly error if the file is too large for the
 *      server's evidence cap (200 MB) — even though narrative
 *      embeds don't go through evidence.ts, the same RAM ceiling
 *      applies because the data URI lives in report_overrides_json
 *      and ships in every render_preview call.
 *
 * The handler is async but the consumer's onPaste callback doesn't
 * need to await it — we call preventDefault() synchronously so the
 * browser doesn't ALSO drop the unhandled paste into the textarea
 * (which would produce a "[object File]" string), then run the
 * encode + splice in the next microtask.
 */

/** Max size for a single embedded media item. 200 MB matches the
 *  server's evidence upload cap (apps/server/src/routes/evidence.ts
 *  MAX_BYTES). Beyond this the data URI bloats the report and the
 *  render_preview request stops fitting in the express body limit. */
const MAX_EMBED_BYTES = 200 * 1024 * 1024;

/** Convert any Blob to a base64 string without going through
 *  readAsDataURL — that path intermittently fails on multi-hundred-MB
 *  videos due to GC stalls in V8 during the string concatenation. */
async function blobToBase64(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const bytes = new Uint8Array(buf);
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const sub = bytes.subarray(i, Math.min(i + CHUNK, bytes.length));
    binary += String.fromCharCode.apply(null, Array.from(sub));
  }
  return btoa(binary);
}

/**
 * Optional uploader for the upload-and-reference paste strategy.
 *
 * When supplied, the paste handler skips inlining the file as a data
 * URI. Instead it hands the blob to the uploader (which typically
 * persists it as evidence on a finding) and inserts a short markdown
 * reference of the form `![caption](evidence:caption)` for images or
 * `[video:evidence:caption]` for videos. The renderer's
 * rewriteEvidenceRefs() helper then resolves that reference at render
 * time by looking up the caption against the finding's attached
 * evidence rows.
 *
 * Why bother — pasting a 2 MB screenshot inlines as a ~2.7 MB base64
 * string in the textarea. The consultant can no longer navigate
 * around it, and every keystroke triggers a debounced auto-save that
 * round-trips the entire blob over the network. The
 * upload-and-reference path keeps the textarea readable AND
 * deduplicates the binary into a single MinIO object that the report
 * embeds at render time.
 *
 * The uploader returns the caption to use in the markdown ref, or
 * null to abort the paste entirely (caller surfaces its own error).
 */
export type PasteUploader = (blob: Blob, suggestedFilename: string) => Promise<string | null>;

/**
 * Paste handler. Returns true if the event was handled (a media file
 * was pasted), false otherwise — letting the consumer decide whether
 * to surface a status message ("Image embedded") or just continue.
 *
 * Usage — data-URI mode (default):
 *   <textarea
 *     onPaste={(e) => handleMediaPaste(e, value, setValue)}
 *   />
 *
 * Usage — upload-and-reference mode (preferred for finding narratives):
 *   <textarea
 *     onPaste={(e) => handleMediaPaste(e, value, setValue, {
 *       uploader: async (blob, name) => {
 *         await invoke('cipherops/evidence/attach', { ... });
 *         return name;  // becomes the markdown ref's caption
 *       },
 *     })}
 *   />
 */
export async function handleMediaPaste(
  e: React.ClipboardEvent<HTMLTextAreaElement>,
  currentValue: string,
  setValue: (next: string) => void,
  options?: { uploader?: PasteUploader },
): Promise<boolean> {
  const items = e.clipboardData?.items;
  if (!items) return false;

  // Find the first image or video file in the clipboard. Most paste
  // events bundle multiple representations (e.g. an image plus its
  // filename as text); we want the file blob.
  let target: { item: DataTransferItem; isImage: boolean; isVideo: boolean } | null = null;
  for (const item of Array.from(items)) {
    if (item.kind !== 'file') continue;
    const isImage = item.type.startsWith('image/');
    const isVideo = item.type.startsWith('video/');
    if (!isImage && !isVideo) continue;
    target = { item, isImage, isVideo };
    break;
  }
  if (!target) return false;

  // Take over the paste — without this the browser will also insert
  // "[object File]" or the file's name as plain text. Run BEFORE the
  // async work so the synchronous default action is cancelled.
  e.preventDefault();

  const blob = target.item.getAsFile();
  if (!blob) return false;

  if (blob.size > MAX_EMBED_BYTES) {
    alert(
      `Pasted media too large: ${(blob.size / 1024 / 1024).toFixed(1)} MB ` +
      `exceeds the ${MAX_EMBED_BYTES / 1024 / 1024} MB embed limit. ` +
      `Trim or transcode the file, or attach via the Evidence panel instead.`,
    );
    return false;
  }

  // Capture cursor position BEFORE the async work — currentTarget may
  // get nulled out by the time the promise resolves.
  const ta = e.currentTarget;
  const cursor = ta.selectionStart ?? currentValue.length;

  try {
    const ext = blob.type.split('/')[1] ?? 'bin';
    const filename = `pasted-${Date.now()}.${ext}`;

    // ─── Path A: upload-and-reference (preferred when an uploader is
    //     wired). The blob gets persisted (typically as evidence) and
    //     the narrative receives a short caption-based reference. The
    //     renderer's rewriteEvidenceRefs resolves it back to a data URI
    //     at render time. Keeps the textarea readable for both
    //     consultant editing AND the round-trip save payload.
    let embed: string;
    if (options?.uploader) {
      const caption = await options.uploader(blob, filename);
      if (caption === null) {
        // Uploader aborted (it surfaced its own error). Bail without
        // splicing anything.
        return false;
      }
      // The `evidence:` URI scheme is a renderer hint — it has no
      // network meaning. rewriteEvidenceRefs treats this as "look up
      // by caption" the same as if the consultant had typed
      // ![caption](anything) manually.
      embed = target.isImage
        ? `![${caption}](evidence:${caption})`
        : `[video:evidence:${caption}]`;
    } else {
      // ─── Path B: legacy data-URI inline. Falls back to dumping the
      //     full base64 into the textarea — only used for narrative
      //     surfaces that don't have an attachable parent (e.g. the
      //     engagement-level Executive Summary, where there's no
      //     finding to attach evidence against). Phase 2 work covers
      //     adding an engagement-media table so this fallback can go
      //     away.
      const b64 = await blobToBase64(blob);
      const dataUri = `data:${blob.type};base64,${b64}`;
      embed = target.isImage
        ? `![${filename}](${dataUri})`
        : `[video:${dataUri}]`;
    }

    // Splice into the textarea at the cursor with surrounding blank
    // lines so the embed renders as its own paragraph in the
    // markdown-to-HTML pass — without the blank lines an image
    // halfway through a sentence stays glued to the prose and
    // breaks the visual rhythm.
    const before = currentValue.slice(0, cursor);
    const after  = currentValue.slice(cursor);
    const padBefore = before.length > 0 && !before.endsWith('\n\n') ? '\n\n' : '';
    const padAfter  = after.length  > 0 && !after.startsWith('\n\n')  ? '\n\n' : '';
    const next = `${before}${padBefore}${embed}${padAfter}${after}`;
    setValue(next);
    return true;
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    alert(`Could not embed pasted media: ${msg}`);
    return false;
  }
}
